// src/components/FoodPhotoReview.js
import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';

function FoodPhotoReview({ token, onBack }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [days, setDays] = useState(7);
  const [withImagesOnly, setWithImagesOnly] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const loadFoodLogs = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        days: days.toString(),
        with_images_only: withImagesOnly.toString(),
      });

      const response = await fetch(`${BACKEND_URL}/api/admin/food-logs?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      } else {
        const txt = await response.text();
        console.error('Error loading food logs:', txt);
      }
    } catch (error) {
      console.error('Error loading food logs:', error);
    } finally {
      setLoading(false);
    }
  }, [token, days, withImagesOnly]);

  useEffect(() => {
    loadFoodLogs();
  }, [loadFoodLogs]);

  const loadLogDetail = useCallback(
    async (logId) => {
      if (!token) return;

      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/food-log/${logId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSelectedLog(data.log);
        } else {
          const txt = await response.text();
          console.error('Error loading log detail:', txt);
        }
      } catch (error) {
        console.error('Error loading log detail:', error);
      }
    },
    [token]
  );

  // Detail View
  if (selectedLog) {
    return (
      <div className="food-review-view">
        <button
          onClick={() => setSelectedLog(null)}
          className="btn-secondary"
          style={{ marginBottom: '24px' }}
          data-testid="back-to-list"
        >
          ← Back to Food Logs
        </button>

        <div
          style={{
            background: 'var(--bg-modal)',
            borderRadius: '16px',
            border: '1px solid var(--border-light)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px',
              borderBottom: '1px solid var(--border-light)',
              background: 'var(--bg-secondary)',
            }}
          >
            <h2 className="heading-3" style={{ margin: 0 }}>
              {selectedLog.food_name}
            </h2>
            <p className="body-small" style={{ color: 'var(--text-secondary)', margin: '8px 0 0 0' }}>
              Logged by {selectedLog.user_name} ({selectedLog.user_email}) on{' '}
              {new Date(selectedLog.created_at).toLocaleString()}
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {/* Image */}
            {selectedLog.image_base64 && (
              <div
                style={{
                  flex: '1 1 400px',
                  padding: '20px',
                  borderRight: '1px solid var(--border-light)',
                }}
              >
                <img
                  src={`data:image/jpeg;base64,${selectedLog.image_base64}`}
                  alt={selectedLog.food_name}
                  style={{
                    width: '100%',
                    maxWidth: '500px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            )}

            {/* Details */}
            <div style={{ flex: '1 1 300px', padding: '20px' }}>
              <h4 className="heading-4" style={{ marginBottom: '16px' }}>
                Nutritional Information
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  ['Calories', `${selectedLog.calories} kcal`],
                  ['Protein', `${selectedLog.protein || 0}g`],
                  ['Carbs', `${selectedLog.carbs || 0}g`],
                  ['Fats', `${selectedLog.fats || 0}g`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                    }}
                  >
                    <span className="body-medium">{label}</span>
                    <span className="body-medium" style={{ fontWeight: 600 }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '24px' }}>
                <h4 className="heading-4" style={{ marginBottom: '12px' }}>
                  Log Details
                </h4>
                <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  <p>
                    <strong>Meal Type:</strong> {selectedLog.meal_type || 'Not specified'}
                  </p>
                  <p>
                    <strong>Date:</strong> {selectedLog.date}
                  </p>
                  <p>
                    <strong>Log ID:</strong> {selectedLog.log_id}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="food-review-view">
      <button onClick={onBack} className="btn-secondary" style={{ marginBottom: '24px' }} data-testid="back-to-admin">
        ← Back to Admin Dashboard
      </button>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <h1 className="heading-2" style={{ margin: 0 }}>
            Food Photo Review
          </h1>
          <p className="body-small" style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
            {logs.length} food log{logs.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="form-input"
            style={{ width: 'auto', padding: '8px 12px', margin: 0 }}
            data-testid="days-filter"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={withImagesOnly}
              onChange={(e) => setWithImagesOnly(e.target.checked)}
              data-testid="images-only-filter"
            />
            <span className="body-small">With photos only</span>
          </label>

          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}
              style={{ padding: '8px 12px' }}
              type="button"
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}
              style={{ padding: '8px 12px' }}
              type="button"
            >
              List
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Loading food logs...
          </p>
        </div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <p className="body-large" style={{ color: 'var(--text-muted)' }}>
            No food logs found for the selected criteria
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
          }}
        >
          {logs.map((log) => (
            <div
              key={log.log_id}
              onClick={() => loadLogDetail(log.log_id)}
              style={{
                background: 'var(--bg-modal)',
                borderRadius: '12px',
                border: '1px solid var(--border-light)',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid={`food-log-card-${log.log_id}`}
            >
              <div
                style={{
                  height: '160px',
                  background: log.has_image
                    ? 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)'
                    : 'var(--bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {log.has_image ? <span style={{ fontSize: '48px' }}>📸</span> : <span style={{ fontSize: '48px', opacity: 0.3 }}>🍽️</span>}
              </div>

              <div style={{ padding: '16px' }}>
                <h3 className="heading-4" style={{ margin: '0 0 8px 0' }}>
                  {log.food_name}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>
                  {log.user_name}
                </p>
                <p className="body-small" style={{ color: 'var(--text-muted)', margin: '0 0 12px 0' }}>
                  {log.date} • {log.meal_type || 'Meal'}
                </p>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    gap: 10,
                  }}
                >
                  <span className="body-small" style={{ whiteSpace: 'nowrap' }}>
                    {log.calories} kcal
                  </span>
                  <span className="body-small" style={{ color: 'var(--text-muted)' }}>
                    P: {log.protein || 0}g • C: {log.carbs || 0}g • F: {log.fats || 0}g
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            background: 'var(--bg-modal)',
            borderRadius: '12px',
            border: '1px solid var(--border-light)',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Food</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>User</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Meal</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Calories</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>Photo</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr
                  key={log.log_id}
                  onClick={() => loadLogDetail(log.log_id)}
                  style={{
                    borderTop: '1px solid var(--border-light)',
                    cursor: 'pointer',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                  onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '12px 16px' }}>{log.food_name}</td>
                  <td style={{ padding: '12px 16px' }}>{log.user_name}</td>
                  <td style={{ padding: '12px 16px' }}>{log.date}</td>
                  <td style={{ padding: '12px 16px' }}>{log.meal_type || '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>{log.calories}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>{log.has_image ? '📸' : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FoodPhotoReview;
